import { useQuery } from '@tanstack/react-query';
import {
  faBriefcase,
  faFileInvoice,
  faCircleCheck,
  faRotateLeft,
  faCalendarDays,
} from '@fortawesome/free-solid-svg-icons';
import { useAuthStore } from '@/stores/auth.store';
import { dashboardService } from '@/services/dashboard.service';
import { KpiCard, KpiCardSkeleton } from './components/KpiCard';
import { QueueTable, QueueTableSkeleton } from './components/QueueTable';
import {
  ModificationRoundQueue,
  ModificationRoundQueueSkeleton,
} from './components/ModificationRoundQueue';
import { RecentActivityFeed, RecentActivitySkeleton } from './components/RecentActivityFeed';

export function CommercialDashboardPage() {
  const user = useAuthStore((s) => s.user);
  const firstName = user?.firstName ?? 'there';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboard-commercial'],
    queryFn: dashboardService.getCommercialDashboard,
    staleTime: 60_000,
  });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Welcome back, {firstName}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Commercial review — quotations and customer approvals.
        </p>
      </div>

      {isError && (
        <div className="flex items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Could not load dashboard. Check your connection and refresh.
        </div>
      )}

      <section>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          KPIs
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => <KpiCardSkeleton key={i} />)
          ) : data ? (
            <>
              <KpiCard
                label="Awaiting Commercial Review"
                value={data.kpis.awaitingCommercialReview}
                icon={faBriefcase}
                iconCls="bg-amber-500/10 text-amber-700"
              />
              <KpiCard
                label="Quotations Pending Upload"
                value={data.kpis.quotationsPendingUpload}
                icon={faFileInvoice}
                iconCls="bg-orange-500/10 text-orange-700"
              />
              <KpiCard
                label="Packages Completed"
                value={data.kpis.commercialPackagesCompleted}
                icon={faCircleCheck}
                iconCls="bg-green-500/10 text-green-700"
              />
              <KpiCard
                label="Returned to Operations"
                value={data.kpis.returnedToOperations}
                icon={faRotateLeft}
                iconCls="bg-red-500/10 text-red-700"
              />
              <KpiCard
                label="Avg Turnaround"
                value={data.kpis.averageTurnaroundDays}
                icon={faCalendarDays}
                iconCls="bg-primary/10 text-primary"
                format="days"
              />
            </>
          ) : null}
        </div>
      </section>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {isLoading ? (
            <>
              <QueueTableSkeleton title="Review Queue" />
              <QueueTableSkeleton title="Awaiting Customer Response" />
              <ModificationRoundQueueSkeleton />
            </>
          ) : data ? (
            <>
              <QueueTable
                title="Review Queue"
                items={data.queues.reviewQueue}
                emptyMessage="No packages awaiting commercial review."
              />
              <QueueTable
                title="Awaiting Customer Response"
                items={data.queues.awaitingCustomerResponse}
                emptyMessage="No packages awaiting customer response."
              />
              {/* D-131 M6 — the round queue replaces the legacy-column
                  "Returned from Customer" table (readiness §14.2). The API still
                  returns `returnedFromCustomer` until M9; it is simply no longer
                  rendered here. */}
              <ModificationRoundQueue
                items={data.queues.modificationRounds ?? []}
                total={data.queues.openModificationRounds ?? 0}
              />
            </>
          ) : null}
        </div>

        <div>
          {isLoading ? (
            <RecentActivitySkeleton />
          ) : data ? (
            <RecentActivityFeed items={data.recentActivity} />
          ) : null}
        </div>
      </section>
    </div>
  );
}
